import { ConversationData } from '@/types/TTextingPlatform';
import { RootState } from '..';

export const selectTextingState = (state: RootState) => state.textingPlatform;

export const selectConversations = (state: RootState) => state.textingPlatform.conversations;

export const selectSelectedClient = (state: RootState) => state.textingPlatform.selectedClient;

export const selectConversationsList = (state: RootState): ConversationData[] => {
	const conversations = state.textingPlatform.conversations;
	return Object.values(conversations).sort((a, b) => {
		if (b.messages.length > 0 && a.messages.length === 0) {
			return 1;
		}

		const aTimestamp =
			a.messages.length > 0 ? new Date(a.messages[a.messages.length - 1].timestamp).getTime() : 0;
		const bTimestamp =
			b.messages.length > 0 ? new Date(b.messages[b.messages.length - 1].timestamp).getTime() : 0;

		return bTimestamp - aTimestamp;
	});
};

export const selectPaginationState = (state: RootState) => ({
	conversationCursor: state.textingPlatform.conversationCursor,
	hasMoreConversations: state.textingPlatform.hasMoreConversations,
	isLoadingMore: state.textingPlatform.isLoadingMore,
});

export const selectConversationByPhone = (state: RootState, phoneNumber: string) =>
	state.textingPlatform.conversations[phoneNumber];

export const selectCurrentConversation = (state: RootState) => {
	const selectedClient = selectSelectedClient(state);
	if (!selectedClient) {
		return null;
	}
	return selectConversationByPhone(state, selectedClient);
};

export const selectCurrentConversationMessages = (state: RootState) => {
	const currentConversation = selectCurrentConversation(state);
	return currentConversation?.messages || [];
};

export const selectFilteredConversations = (
	state: RootState,
	searchTerm: string = '',
	showUnrepliedOnly: boolean = false,
	savedContacts: Record<string, string> = {},
	provisionedNumber: string = '',
): ConversationData[] => {
	const conversations = selectConversationsList(state);

	return conversations.filter((conversation) => {
		const contactNameOrNumber =
			savedContacts[conversation.client_number] || conversation.client_number || '';

		if (showUnrepliedOnly) {
			const hasMessages = conversation.messages.length > 0;
			const lastMessage = hasMessages
				? conversation.messages[conversation.messages.length - 1]
				: null;
			const isLastMessageFromClient = lastMessage?.from !== provisionedNumber;

			if (!hasMessages || !isLastMessageFromClient) {
				return false;
			}
		}

		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			const numberMatch = conversation.client_number.includes(searchTerm);
			const nameMatch = contactNameOrNumber.toLowerCase().includes(searchLower);

			return numberMatch || nameMatch;
		}

		return true;
	});
};

export const selectConversationCount = (state: RootState) => {
	return Object.keys(state.textingPlatform.conversations).length;
};

export const selectUnreadConversationsCount = (
	state: RootState,
	provisionedNumber: string = '',
): number => {
	const conversations = selectConversationsList(state);

	return conversations.filter((conversation) => {
		const hasMessages = conversation.messages.length > 0;
		if (!hasMessages) {
			return false;
		}

		const lastMessage = conversation.messages[conversation.messages.length - 1];
		return lastMessage.from !== provisionedNumber;
	}).length;
};
